import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/nurseryApi";
import { ArrowLeft, FloppyDisk, Trash, Phone, EnvelopeSimple, MapPin, Globe, ShieldCheck, Star, ChatCircleDots, Leaf, Package, Users as UsersIcon, ChatTeardropDots } from "@phosphor-icons/react";

const SEV_COLOR = { healthy: "var(--success)", low: "#93B17D", moderate: "var(--accent)", severe: "var(--destructive)" };

export default function AdminUserDetail() {
  const { uid } = useParams();
  const nav = useNavigate();
  const [u, setU] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/users/${uid}`).then((r) => setU(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  useEffect(() => { load(); }, [uid]);

  if (err) return <div data-testid="admin-user-detail-error" style={{ padding: 24 }}>{err} — <Link to="/admin/users">back</Link></div>;
  if (!u) return <div style={{ padding: 24 }}>Loading…</div>;

  const set = (k) => (e) => setU({ ...u, [k]: e.target.value });
  const setBool = (k) => (e) => setU({ ...u, [k]: e.target.checked });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${uid}`, {
        nursery_name: u.nursery_name, owner_name: u.owner_name,
        phone: u.phone, address: u.address, website: u.website,
        is_admin: !!u.is_admin, is_founding: !!u.is_founding,
        chatbot_enabled: !!u.chatbot_enabled, admin_notes: u.admin_notes || "",
        plan: u.plan || "",
      });
    } finally {
      setSaving(false);
    }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    await api.delete(`/admin/users/${uid}`);
    nav("/admin/users");
  };

  return (
    <div data-testid="admin-user-detail-page" className="fade-up">
      <Link to="/admin/users" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--secondary)", textDecoration: "none" }} data-testid="back-to-users">
        <ArrowLeft size={12} weight="bold" /> All nurseries
      </Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 14, flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h1 className="page-title">{u.nursery_name}</h1>
            {u.is_admin && <span className="badge badge-warn"><ShieldCheck size={11} weight="bold" /> admin</span>}
            {u.is_founding && <span className="badge badge-success"><Star size={11} weight="fill" /> Founding</span>}
            {u.chatbot_enabled && <span className="badge badge-soft"><ChatCircleDots size={11} weight="bold" /> Chatbot</span>}
          </div>
          <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 14.5 }}>Owner: <b style={{ color: "var(--text)" }}>{u.owner_name}</b> · joined {new Date(u.created_at).toLocaleDateString()}</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-outline" onClick={del} disabled={u.is_admin} data-testid="delete-user-btn"><Trash size={14} weight="bold" /> Delete</button>
          <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="save-user-btn"><FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20, marginTop: 28 }} className="detail-grid">
        {/* LEFT: editable account info */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title="Account">
            <Row>
              <Field label="Nursery name" v={u.nursery_name} on={set("nursery_name")} testid="ud-nursery" />
              <Field label="Owner" v={u.owner_name} on={set("owner_name")} testid="ud-owner" />
            </Row>
            <Row>
              <Field label="Phone" v={u.phone || ""} on={set("phone")} testid="ud-phone" />
              <Field label="Email (read-only)" v={u.email} on={() => {}} disabled />
            </Row>
            <Field label="Address" v={u.address || ""} on={set("address")} testid="ud-address" />
            <Field label="Website" v={u.website || ""} on={set("website")} testid="ud-website" />
          </Card>

          <Card title="Plan & access">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label className="label">Plan</label>
                <select className="select" value={u.plan || ""} onChange={set("plan")} data-testid="ud-plan">
                  <option value="">— none —</option>
                  <option value="founding">Founding ($199/mo locked)</option>
                  <option value="starter">Starter ($199/mo)</option>
                  <option value="pro">Pro ($399/mo)</option>
                  <option value="enterprise">Enterprise ($1,299/mo)</option>
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
                <Toggle label="Founding Nursery" v={u.is_founding} on={setBool("is_founding")} testid="ud-founding" />
                <Toggle label="Chatbot add-on" v={u.chatbot_enabled} on={setBool("chatbot_enabled")} testid="ud-chatbot" />
                <Toggle label="Platform admin" v={u.is_admin} on={setBool("is_admin")} testid="ud-admin" />
              </div>
            </div>
          </Card>

          <Card title="Private admin notes">
            <textarea className="textarea" rows={4} value={u.admin_notes || ""} onChange={set("admin_notes")} placeholder="Visit notes, follow-ups, gut feel — only you can see this." data-testid="ud-notes" />
          </Card>
        </div>

        {/* RIGHT: live data */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title="Contact" pad>
            <ContactRow icon={EnvelopeSimple} text={u.email} href={`mailto:${u.email}`} />
            {u.phone && <ContactRow icon={Phone} text={u.phone} href={`tel:${u.phone}`} />}
            {u.address && <ContactRow icon={MapPin} text={u.address} />}
            {u.website && <ContactRow icon={Globe} text={u.website.replace(/^https?:\/\//, "")} href={u.website} external />}
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <MetricCard icon={Leaf} label="Plant scans" value={(u.scans || []).length} />
            <MetricCard icon={Package} label="Inventory" value={(u.inventory || []).length} />
            <MetricCard icon={UsersIcon} label="Leads" value={(u.leads || []).length} />
            <MetricCard icon={ChatTeardropDots} label="Shrinkage logs" value={(u.shrinkage || []).length} />
          </div>

          {u.scans?.length > 0 && (
            <Card title="Recent scans">
              {u.scans.slice(0, 5).map((s) => (
                <div key={s.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{s.issue}</div>
                    <div style={{ fontSize: 12, color: "var(--muted-fg)" }}>{s.plant} · {new Date(s.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className="badge" style={{ background: SEV_COLOR[s.severity], color: "white" }}>{s.severity}</span>
                </div>
              ))}
            </Card>
          )}

          {u.leads?.length > 0 && (
            <Card title="Customer leads (their own)">
              {u.leads.slice(0, 5).map((l) => (
                <div key={l.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{l.name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted-fg)" }}>{l.contact}</div>
                  <div style={{ fontSize: 12, color: "var(--text-soft)", marginTop: 4 }}>{l.interest}</div>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>

      <style>{`@media(max-width:1000px){.detail-grid{grid-template-columns:1fr !important;}}`}</style>
    </div>
  );
}

const Card = ({ title, children, pad }) => (
  <div className="card" style={{ padding: pad ? 18 : 22 }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--secondary)", marginBottom: 14 }}>{title}</div>
    {children}
  </div>
);
const Row = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>{children}</div>;
const Field = ({ label, v, on, testid, disabled }) => (
  <div>
    <label className="label">{label}</label>
    <input className="input" value={v} onChange={on} disabled={disabled} data-testid={testid} />
  </div>
);
const Toggle = ({ label, v, on, testid }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
    <input type="checkbox" checked={!!v} onChange={on} data-testid={testid} />
    {label}
  </label>
);
const ContactRow = ({ icon: Icon, text, href, external }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0", fontSize: 14 }}>
    <Icon size={16} weight="fill" color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
    {href ? <a href={href} target={external ? "_blank" : undefined} rel="noreferrer" style={{ color: "var(--text)", textDecoration: "underline", wordBreak: "break-all" }}>{text}</a> : <span style={{ color: "var(--text-soft)" }}>{text}</span>}
  </div>
);
const MetricCard = ({ icon: Icon, label, value }) => (
  <div className="card" style={{ padding: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Icon size={18} weight="fill" color="var(--primary)" />
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)" }}>{label}</span>
    </div>
    <div className="font-display" style={{ fontSize: 32, marginTop: 6, lineHeight: 1 }}>{value}</div>
  </div>
);
